import React, { useState, useEffect } from "react";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
import toast from "react-hot-toast";
import { getAnnouncementApi, createAnnouncementApi, updateAnnouncementApi, deleteAnnouncementApi } from "../../helper/apiHelper";

const AnnouncementManagement = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newAnnouncement, setNewAnnouncement] = useState({ title: '', redirectionUrl: '', id: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Fetch announcements on mount
    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            setIsPageLoading(true);
            const response = await getAnnouncementApi();
            setAnnouncements(response.data || []);
        } catch (error) {
            toast.error(error || "Failed to fetch announcements");
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleCreateOrUpdate = async () => {
        const { title, redirectionUrl, id } = newAnnouncement;

        if (!title) {
            toast.error("Please enter title");
            return;
        }
        if (!redirectionUrl) {
            toast.error("Please enter redirection URL");
            return;
        }

        try {
            setIsLoading(true);
            const payload = { title, redirectionUrl };

            if (isEditing) {
                await updateAnnouncementApi(payload, id);
                toast.success("Announcement updated successfully!");
            } else {
                await createAnnouncementApi(payload);
                toast.success("Announcement created successfully!");
            }

            setShowAddModal(false);
            setNewAnnouncement({ title: '', redirectionUrl: '', id: '' });
            setIsEditing(false);
            fetchAnnouncements(); // Refresh the list
        } catch (error) {
            toast.error(error || "Action failed");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewAnnouncement((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditClick = (item) => {
        setIsEditing(true);
        setShowAddModal(true);
        setNewAnnouncement({
            title: item.title,
            redirectionUrl: item.redirectionUrl,
            id: item.id,
        });
    }

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        try {
            setIsLoading(true);
            await deleteAnnouncementApi(itemToDelete.id);
            toast.success("Announcement deleted successfully");
            setShowDeleteModal(false);
            setItemToDelete(null);
            fetchAnnouncements(); // Refresh the list
        } catch (error) {
            toast.error(error || "Failed to delete announcement");
        } finally {
            setIsLoading(false);
        }
    }

    const filteredItems = announcements?.filter((item) => {
        const search = searchTerm?.toLowerCase();
        return item.title?.toLowerCase().includes(search);
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const totalItems = filteredItems?.length || 0;
    const itemsPerPageSafe = itemsPerPage || 1;
    const totalPages = Math.ceil(totalItems / itemsPerPageSafe);
    const startIdx = (currentPage - 1) * itemsPerPage;

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <MasterLayout>
            <Breadcrumb title="Announcement Management" />

            <div className="col-lg-12">
                <div className="card">
                    <div className="card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                        <form className="navbar-search d-flex align-items-center gap-2">
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto form-control"
                                name="search"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>

                        <button
                            type="button"
                            className="btn btn-primary-600 radius-8 px-20 py-11"
                            onClick={() => {
                                setIsEditing(false);
                                setNewAnnouncement({ title: '', redirectionUrl: '', id: '' });
                                setShowAddModal(true);
                            }}
                        >
                            + Create Announcement
                        </button>

                    </div>
                    <div className="card-body">
                        {isPageLoading ? (
                            <div className="text-center py-40">
                                <span className="spinner-border spinner-border-lg text-primary-600" role="status" />
                                <p className="text-muted mt-3">Loading announcements...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    {filteredItems?.length === 0 ? (
                                        <div className="text-center py-40 text-muted fw-medium">No announcement found.</div>
                                    ) : (
                                        <table className="table basic-border-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Title</th>
                                                    <th>Redirection URL</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredItems?.slice(startIdx, startIdx + itemsPerPage)?.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.title}</td>
                                                        <td>
                                                            <a href={item.redirectionUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600">
                                                                {item.redirectionUrl}
                                                            </a>
                                                        </td>
                                                        <td className="text-start">
                                                            <div className="d-flex align-items-start gap-10 justify-content-start">
                                                                <button
                                                                    type="button"
                                                                    className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    onClick={() => handleEditClick(item)}
                                                                >
                                                                    <Icon icon="lucide:edit" className="menu-icon" />
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteClick(item)}
                                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                >
                                                                    <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {filteredItems?.length > itemsPerPage &&
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-24 mb-10">
                                        <span>
                                            Showing {startIdx + 1} to{" "}
                                            {Math.min(startIdx + itemsPerPage, filteredItems?.length)} of{" "}
                                            {filteredItems?.length} entries
                                        </span>
                                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                                            <li className="page-item">
                                                <button
                                                    className="page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px bg-base"
                                                    onClick={() => goToPage(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    <Icon icon="ep:d-arrow-left" className="text-xl" />
                                                </button>
                                            </li>

                                            {[...Array(totalPages)].map((_, index) => (
                                                <li className="page-item" key={index}>
                                                    <button
                                                        className={`page-link ${currentPage === index + 1
                                                            ? "bg-primary-600 text-white"
                                                            : "bg-primary-50 text-secondary-light"
                                                            } fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px`}
                                                        onClick={() => goToPage(index + 1)}
                                                    >
                                                        {index + 1}
                                                    </button>
                                                </li>
                                            ))}

                                            <li className="page-item">
                                                <button
                                                    className="page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px bg-base"
                                                    onClick={() => goToPage(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    <Icon icon="ep:d-arrow-right" className="text-xl" />
                                                </button>
                                            </li>
                                        </ul>
                                    </div>}
                            </>
                        )}

                    </div>
                </div>
            </div>

            {showAddModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div
                            className="modal-dialog modal-md modal-dialog-centered"
                            style={{ zIndex: 11122 }}
                        >
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">{isEditing ? "Update Announcement" : "Add Announcement"}</h1>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowAddModal(false)}
                                    />
                                </div>

                                <div className="modal-body p-24">
                                    <form className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Title</label>
                                            <input
                                                type="text"
                                                name="title"
                                                className="form-control"
                                                value={newAnnouncement.title}
                                                onChange={handleChange}
                                                placeholder="Enter Title"
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Redirection URL</label>
                                            <input
                                                type="text"
                                                name="redirectionUrl"
                                                className="form-control"
                                                value={newAnnouncement.redirectionUrl}
                                                onChange={handleChange}
                                                placeholder="Enter Redirection URL"
                                            />
                                        </div>

                                        <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                                            <button
                                                type="button"
                                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-24 py-10 radius-8"
                                                onClick={() => {
                                                    setShowAddModal(false);
                                                    setNewAnnouncement({ title: '', redirectionUrl: '', id: '' });
                                                    setIsEditing(false);
                                                }}
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-primary border border-primary-600 text-md px-32 py-10 radius-8 d-flex align-items-center gap-2"
                                                onClick={handleCreateOrUpdate}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm" role="status" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    isEditing ? 'Update' : 'Save'
                                                )}
                                            </button>

                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        <div
                            className="modal-backdrop fade show"
                            style={{ zIndex: 1040 }}
                        />
                    </div>
                </>
            )}

            {showDeleteModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div
                            className="modal-dialog modal-md modal-dialog-centered"
                            style={{ zIndex: 11122 }}
                        >
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">Confirm Delete</h1>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setItemToDelete(null);
                                        }}
                                    />
                                </div>

                                <div className="modal-body p-24">
                                    <p className="text-secondary-light mb-0">
                                        Are you sure you want to delete the announcement <strong>"{itemToDelete?.title}"</strong>? This action cannot be undone.
                                    </p>
                                </div>

                                <div className="modal-footer py-16 px-24 border-top">
                                    <button
                                        type="button"
                                        className="border border-neutral-300 bg-hover-neutral-200 text-secondary-light text-md px-24 py-10 radius-8"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setItemToDelete(null);
                                        }}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-danger border border-danger-600 text-md px-32 py-10 radius-8 d-flex align-items-center gap-2"
                                        onClick={confirmDelete}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" />
                                                Deleting...
                                            </>
                                        ) : (
                                            'Delete'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div
                            className="modal-backdrop fade show"
                            style={{ zIndex: 1040 }}
                        />
                    </div>
                </>
            )}

        </MasterLayout>
    );
};

export default AnnouncementManagement;
